import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import type { AppDispatch } from 'src/redux/store';
import {
  loadSectionSummary,
  selectSectionSummary,
} from 'src/redux/slices/DataVault/dataVaultAiSummarySlice';
import styles from './AISectionSummary.module.css';

interface Props {
  surveyId: number;
  sectionId: string;
  compact?: boolean;
}

function AISectionSummary({
  surveyId,
  sectionId,
  compact = false,
}: Props): React.ReactElement | null {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const numericSectionId = parseInt(sectionId, 10);
  const sectionSelector = useMemo(
    () => selectSectionSummary(surveyId, numericSectionId),
    [surveyId, numericSectionId]
  );
  const entry = useSelector(sectionSelector);

  useEffect(() => {
    if (!surveyId) {
      return;
    }
    if (isNaN(numericSectionId)) {
      return;
    }
    if (
      entry?.status === 'success' ||
      entry?.status === 'loading' ||
      entry?.status === 'error'
    ) {
      return;
    }
    dispatch(loadSectionSummary({ surveyId, sectionId: numericSectionId }));
  }, [dispatch, surveyId, numericSectionId, entry?.status]);

  if (isNaN(numericSectionId)) {
    return null;
  }

  if (entry === undefined || entry.status === 'loading') {
    return (
      <>
        <Divider />
        <Box sx={{ pt: 1.5, px: 2, pb: '14px' }}>
          <SummarySkeleton />
        </Box>
      </>
    );
  }

  if (entry.status === 'error') {
    return null;
  }

  const { data } = entry;
  if (!data || data.empty) {
    return null;
  }

  if (compact) {
    return (
      <>
        <Divider />
        <Box
          component="section"
          className={styles.summary}
          aria-label="Section summary"
          data-rum-mask
          sx={{ pt: 1.5, px: 2, pb: '14px' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            role="button"
            tabIndex={0}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCollapsed((c) => !c);
              }
            }}
            sx={{ cursor: 'pointer', mb: collapsed ? 0 : 0.75 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Sparkles size={12} color="#8B5CF6" />
              <Typography
                sx={{
                  fontSize: 11,
                  color: '#8B5CF6',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                }}
              >
                Summary
              </Typography>
            </Stack>
            {collapsed ? (
              <ChevronDown size={12} color="#8B5CF6" />
            ) : (
              <ChevronUp size={12} color="#8B5CF6" />
            )}
          </Stack>
          {!collapsed && (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {data.summary}
            </Typography>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box
      component="section"
      className={styles.summary}
      aria-label="Section summary"
      data-rum-mask
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: 0.75 }}
      >
        <Sparkles size={12} color="#8B5CF6" />
        <Typography
          sx={{
            fontSize: 11,
            color: '#8B5CF6',
            fontWeight: 600,
            letterSpacing: '0.3px',
          }}
        >
          Summary
        </Typography>
      </Stack>
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {data.summary}
      </Typography>
    </Box>
  );
}

function SummarySkeleton(): React.ReactElement {
  return (
    <div className={styles.skeleton} aria-hidden>
      <div />
      <div />
      <div style={{ width: '60%' }} />
    </div>
  );
}

export default AISectionSummary;
